import { Button } from "components/DesignSystem/Button/Button";
import { useItemContext } from "components/DesignSystem/FocusBlock/FocusBlock";
import { FormikForm } from "components/FormikForm/FormikForm";
import { LongArrowRight } from "components/icons/LongArrowRight";
import { Refresh } from "components/icons/Refresh";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { FormikValues } from "formik";
import { NumericFormat } from "react-number-format";
import { useUpdateCustomExchangeRateMutation } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import ToolTip from "components/design/toolTip";

type CustomExchangeRateFocusBlockProps = {
  invoiceId: string;
  currency: string;
  currentRate?: number;
  originalRate?: number;
};

export const ExchangeRateContent = ({
  invoiceId,
  currency,
  currentRate,
  originalRate,
}: CustomExchangeRateFocusBlockProps) => {
  const { close } = useItemContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();

  const [updateExchangeRate, { isLoading: isUpdating }] =
    useUpdateCustomExchangeRateMutation();

  const handleApply = async (values: FormikValues) => {
    if (!entityId || !values.rate) return;

    try {
      await updateExchangeRate({
        entityId,
        invoiceId,
        exchangeRate: values.rate,
      }).unwrap();
      successToast({
        message: `Exchange rate updated to ${values.rate} ${currency} for this invoice`,
      });
      close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <FormikForm
      initialValues={{ rate: currentRate || null }}
      onSubmit={handleApply}
    >
      {({ values, setFieldValue, submitForm }) => {
        const handleReset = () => {
          if (originalRate) {
            setFieldValue("rate", originalRate);
          }
        };

        return (
          <div className="t-py-4 t-bg-white">
            <div className="t-text-subtitle-lg t-text-text-100 t-mb-4">
              Set custom exchange rate
            </div>
            <div className="t-flex t-items-center t-gap-4 t-mb-4">
              <div className="t-text-subtext t-text-text-60">
                USD 1<sup className="t-text-[8px] ">.00</sup>
              </div>
              <div className="t-text-neutral-20">
                <LongArrowRight className="t-stroke-1" />
              </div>
              <div className="t-flex t-items-center t-border t-border-solid t-border-neutral-10 t-rounded-md t-overflow-hidden t-flex-1">
                <div className="t-bg-surface-lighter-grey t-px-3 t-py-2 t-text-body-sm t-text-text-30 t-border-0 t-border-r t-border-solid t-border-neutral-10">
                  {currency}
                </div>
                <NumericFormat
                  value={values.rate}
                  onValueChange={(numericValues) => {
                    setFieldValue("rate", numericValues.floatValue || null);
                  }}
                  className="all:unset t-w-full t-px-3 t-py-2 t-text-body-sm"
                  allowNegative={false}
                  placeholder="0.00"
                />
                <ToolTip text="Reset to default rate">
                  <span className="t-pr-1">
                    <Button
                      type="button"
                      customType="ghost_icon"
                      size="small"
                      onClick={handleReset}
                    >
                      <span style={{ transform: "scaleX(-1)" }}>
                        <Refresh size="16" stroke="1.5" />
                      </span>
                    </Button>
                  </span>
                </ToolTip>
              </div>
            </div>
            <div className="t-flex t-gap-3 t-items-center">
              <Button
                customType="primary_outlined"
                size="small"
                onClick={submitForm}
                disabled={isUpdating || !values.rate}
                isLoading={isUpdating}
              >
                Apply rate
              </Button>
              <Button customType="secondary" size="small" onClick={close}>
                Cancel
              </Button>
            </div>
          </div>
        );
      }}
    </FormikForm>
  );
};
